import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Mail, Lock, ArrowLeft, Link2, Copy, Package } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

export default function Login() {
  const navigate = useNavigate();
  const { login, requestMagicLink } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [devLink, setDevLink] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}`);
      navigate(user.role === "admin" || user.role === "dispatcher" ? "/ops"
        : user.role === "accountant" ? "/accounting" : "/customer");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const sendLink = async () => {
    if (!email.trim()) { toast.error("Enter your email first"); return; }
    setLinkBusy(true);
    try {
      const r = await requestMagicLink(email.trim());
      setDevLink(r.dev_link || "");
      toast.success("Sign-in link sent to your email");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLinkBusy(false); }
  };

  return (
    <div className="min-h-screen flex grain relative">
      <div className="hidden lg:block lg:w-1/2 bg-card border-r border-border relative overflow-hidden">
        <div className="absolute inset-0 tape-stripe opacity-10" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between">
          <Link to="/"><BrandLogo className="h-12" /></Link>
          <div>
            <h1 className="text-6xl xl:text-7xl font-display uppercase leading-[0.9] tracking-tighter mb-6">
              Your<br/>
              <span className="text-brand-green">Deliveries.</span>
            </h1>
            <p className="text-muted-foreground max-w-md text-lg">
              Sign in to track your shipments in real time, view invoices, and manage your account with Country Courier.
            </p>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground flex items-center gap-2">
            <Package className="w-4 h-4 text-brand-green" /> British Columbia // Canada
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md space-y-6">
          <Link to="/" className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground flex items-center gap-2" data-testid="back-home-link">
            <ArrowLeft className="w-3 h-3" /> Back
          </Link>
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-brand-green mb-2">// CUSTOMER SIGN IN</div>
            <h2 className="text-4xl md:text-5xl font-display uppercase tracking-tighter">Welcome Back</h2>
            <p className="text-sm text-muted-foreground mt-2">Access your deliveries, tracking and invoices.</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-mono text-xs uppercase tracking-widest">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="h-14 pl-10 bg-white border-2" data-testid="login-email-input"/>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pwd" className="font-mono text-xs uppercase tracking-widest">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="pwd" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-14 pl-10 bg-white border-2" data-testid="login-password-input"/>
              </div>
            </div>
            <Button type="submit" disabled={busy} className="w-full h-14 bg-brand-green text-black hover:bg-brand-green/90 font-mono uppercase tracking-wider font-bold" data-testid="login-submit-btn">
              {busy ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="space-y-3">
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">No password? Get a secure sign-in link by email.</p>
            <Button type="button" variant="outline" disabled={linkBusy} onClick={sendLink} className="w-full h-14 border-2 font-mono uppercase tracking-wider font-bold" data-testid="send-magic-link-btn">
              <Link2 className="w-4 h-4 mr-2" />{linkBusy ? "Sending..." : "Email me a sign-in link"}
            </Button>
          </div>

          {devLink && (
            <div className="border-2 border-dashed border-brand-green p-3 bg-brand-green/5" data-testid="dev-magic-link">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2"><Link2 className="w-3 h-3"/>MOCK EMAIL — click to sign in</div>
              <a href={devLink} className="font-mono text-xs text-brand-green break-all underline" data-testid="dev-magic-link-href">{devLink}</a>
              <Button size="sm" variant="outline" onClick={()=>navigator.clipboard.writeText(devLink)} className="mt-2 font-mono uppercase text-[10px]"><Copy className="w-3 h-3 mr-1"/>Copy</Button>
            </div>
          )}

          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground pt-2 border-t border-border">
            Driver? <Link to="/driver/login" className="text-brand-green hover:underline" data-testid="login-driver-link">Phone OTP login</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
